import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RefreshCw, Shield, Star, ChevronRight } from 'lucide-react';
import Header from './components/Header11';
import Footer from './components/Footer11';
import ProductCard from './components/ProductCard';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);

    const product = {
        id: id,
        name: 'Silk Evening Gown',
        price: 8999,
        originalPrice: 12999,
        rating: 5,
        reviews: 47,
        description: 'An exquisite evening gown crafted from the finest Italian silk. This timeless piece embodies sophistication and elegance, perfect for the most prestigious occasions.',
        features: [
            'Pure Italian silk fabric',
            'Hand-finished seams',
            'Tailored fit',
            'Concealed zipper closure',
            'Dry clean only',
        ],
        images: [
            'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=800',
            'https://images.unsplash.com/photo-1539008835657-9e8e9680c956?w=800',
            'https://images.unsplash.com/photo-1566174053879-31528523f8ae?w=800',
            'https://images.unsplash.com/photo-1572804013427-4d7ca7268217?w=800',
        ],
        sizes: ['XS', 'S', 'M', 'L'],
        inStock: true,
    };

    const relatedProducts = [
        {
            id: 2,
            name: 'Cashmere Blazer',
            category: 'Men',
            price: 15999,
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600',
        },
        {
            id: 3,
            name: 'Leather Clutch',
            category: 'Accessories',
            price: 4999,
            image: 'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?w=600',
        },
        {
            id: 4,
            name: 'Pearl Necklace',
            category: 'Accessories',
            price: 18999,
            image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=600',
        },
        {
            id: 5,
            name: 'Italian Loafers',
            category: 'Footwear',
            price: 12999,
            image: 'https://images.unsplash.com/photo-1533867617858-e7b97e060509?w=600',
        },
    ];

    return (
        <div className="min-h-screen bg-gray-900 text-white font-serif">
            <Header showBackButton={true} cartCount={3} />

            <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
                {/* Product Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-20">
                    {/* Images */}
                    <div>
                        {/* Main Image */}
                        <div className="bg-gray-800 mb-6 overflow-hidden aspect-[3/4]">
                            <img
                                src={product.images[selectedImage]}
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Thumbnail Gallery */}
                        <div className="grid grid-cols-4 gap-4">
                            {product.images.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedImage(index)}
                                    className={`bg-gray-800 overflow-hidden border-2 aspect-square transition-colors ${
                                        selectedImage === index ? 'border-white' : 'border-gray-700 hover:border-gray-600'
                                    }`}
                                >
                                    <img src={image} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Product Info */}
                    <div>
                        <p className="text-sm tracking-widest mb-4 text-white/70">PRESTIGE COLLECTION</p>
                        <h1 className="text-4xl lg:text-5xl font-light mb-6">{product.name}</h1>

                        {/* Rating */}
                        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-700">
                            <div className="flex gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        size={16}
                                        strokeWidth={1.5}
                                        className={i < Math.floor(product.rating) ? 'fill-white text-white' : 'text-gray-600'}
                                    />
                                ))}
                            </div>
                            <span className="text-sm text-white/60 font-light">({product.reviews} reviews)</span>
                        </div>

                        {/* Price */}
                        <div className="mb-8">
                            <div className="flex items-baseline gap-4 mb-2">
                                <span className="text-4xl font-light">₹{product.price.toLocaleString()}</span>
                                {product.originalPrice && (
                                    <span className="text-xl text-white/40 line-through font-light">₹{product.originalPrice.toLocaleString()}</span>
                                )}
                            </div>
                            {product.originalPrice && (
                                <p className="text-sm text-white/60 font-light tracking-wide">
                                    Save ₹{(product.originalPrice - product.price).toLocaleString()}
                                </p>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-white/80 mb-8 leading-relaxed font-light">{product.description}</p>

                        {/* Size Selector */}
                        <div className="mb-8">
                            <label className="block text-sm font-light text-white/70 mb-4 tracking-wider">SELECT SIZE</label>
                            <div className="flex gap-3">
                                {product.sizes.map(size => (
                                    <button
                                        key={size}
                                        onClick={() => setSelectedSize(size)}
                                        className={`px-6 py-3 border font-light transition-colors ${
                                            selectedSize === size
                                                ? 'border-white bg-white text-gray-900'
                                                : 'border-gray-700 hover:border-white'
                                        }`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Quantity */}
                        <div className="mb-10">
                            <label className="block text-sm font-light text-white/70 mb-4 tracking-wider">QUANTITY</label>
                            <div className="flex items-center gap-6">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="w-12 h-12 border border-gray-700 hover:border-white font-light transition-colors"
                                >
                                    −
                                </button>
                                <span className="text-xl font-light w-12 text-center">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="w-12 h-12 border border-gray-700 hover:border-white font-light transition-colors"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4 mb-10">
                            <button
                                onClick={() => navigate('/template11/cart')}
                                className="flex-1 group bg-white text-gray-900 py-4 font-light tracking-widest text-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-3"
                            >
                                <ShoppingCart size={20} strokeWidth={1.5} />
                                ADD TO CART
                                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button className="w-14 h-14 border border-gray-700 hover:border-white flex items-center justify-center transition-colors group">
                                <Heart size={20} strokeWidth={1.5} className="group-hover:fill-white transition-all" />
                            </button>
                        </div>

                        {/* Features */}
                        <div className="border-t border-gray-700 pt-8 mb-8">
                            <h3 className="font-light text-sm tracking-wider text-white/70 mb-6">CRAFTSMANSHIP</h3>
                            <ul className="space-y-3">
                                {product.features.map((feature, index) => (
                                    <li key={index} className="flex items-start gap-3 text-white/80 font-light">
                                        <span className="text-white/40">•</span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Info Cards */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-6 border border-gray-700">
                                <Truck size={24} strokeWidth={1.5} className="mx-auto mb-3 text-white" />
                                <p className="text-xs text-white/60 font-light tracking-wide">Complimentary Shipping</p>
                            </div>
                            <div className="text-center p-6 border border-gray-700">
                                <RefreshCw size={24} strokeWidth={1.5} className="mx-auto mb-3 text-white" />
                                <p className="text-xs text-white/60 font-light tracking-wide">Easy Returns</p>
                            </div>
                            <div className="text-center p-6 border border-gray-700">
                                <Shield size={24} strokeWidth={1.5} className="mx-auto mb-3 text-white" />
                                <p className="text-xs text-white/60 font-light tracking-wide">Secure Payment</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                <div>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl lg:text-4xl font-light mb-4">Curated for You</h2>
                        <div className="w-24 h-px bg-white/30 mx-auto"></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
                        {relatedProducts.map(product => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ProductDetail;
